import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { DeveloperNav } from '@/components/DeveloperNav';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Code, 
  Key, 
  BarChart3, 
  CheckCircle, 
  XCircle, 
  Clock,
  Copy,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Book,
  HelpCircle,
  AlertTriangle,
  Mail,
  ExternalLink
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface DeveloperAccount {
  id: number;
  companyName?: string;
  website?: string;
  description?: string;
  useCase?: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  tier: 'free' | 'pro' | 'enterprise';
  monthlyLimit: number;
  rejectionReason?: string;
  createdAt: string;
}

interface ApiKey {
  id: number;
  keyName: string;
  keyPrefix: string;
  permissions: Record<string, boolean>;
  isActive: boolean;
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
}

interface UsageStats {
  totalCalls: number;
  successfulCalls: number;
  errorCalls: number;
  averageResponseTime: number;
  totalRequestSize: number;
  totalResponseSize: number;
  dailyUsage: Record<string, number>;
}

export default function DeveloperDashboard() {
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<Record<string, boolean>>({});
  const [newKeyExpiresAt, setNewKeyExpiresAt] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<number | null>(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [developerAccount, setDeveloperAccount] = useState<DeveloperAccount | null>(null);
  const hasFetchedAccount = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasFetchedAccount.current) {
      hasFetchedAccount.current = true;
      fetch('/api/developer/account', { credentials: 'include' })
        .then(res => {
          if (res.ok) return res.json();
          return null;
        })
        .then(data => {
          if (data?.data) {
            setDeveloperAccount(data.data);
          } else {
            setLocation('/developer-signup');
          }
        })
        .catch(() => {
          setLocation('/developer-signup');
        })
        .finally(() => setAccountLoading(false));
    } else if (!isAuthenticated) {
      setAccountLoading(false);
    }
  }, [isAuthenticated, setLocation]);

  const { data: apiKeysData, isLoading: keysLoading } = useQuery<{ success: boolean; data: ApiKey[] }>({
    queryKey: ['/api/developer/api-keys'],
    enabled: isAuthenticated && developerAccount?.status === 'approved',
    retry: false,
    refetchOnWindowFocus: false,
  });
  const apiKeys: ApiKey[] = apiKeysData?.data || [];

  const { data: usageStatsData, isLoading: statsLoading } = useQuery<{ success: boolean; data: { usageStats: UsageStats } }>({
    queryKey: ['/api/developer/dashboard'],
    enabled: isAuthenticated && developerAccount?.status === 'approved',
    retry: false,
    refetchOnWindowFocus: false,
  });
  const usageStats: UsageStats | null = usageStatsData?.data?.usageStats || null;

  const loading = accountLoading;

  const createApiKeyMutation = useMutation({
    mutationFn: async (data: { keyName: string; permissions: Record<string, boolean>; expiresAt?: string }) => {
      const response = await apiRequest('POST', '/api/developer/api-keys', data);
      return response.json();
    },
    onSuccess: (data) => {
      setCreatedApiKey(data.data.apiKey);
      setShowApiKey(true);
      setNewKeyName('');
      setNewKeyPermissions({});
      setNewKeyExpiresAt('');
      setShowCreateKey(false);
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'API Key Created',
        description: 'Your new API key has been created. Copy it now - it will not be shown again.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create API key',
        variant: 'destructive',
      });
    },
  });

  const toggleApiKeyMutation = useMutation({
    mutationFn: async ({ keyId, isActive }: { keyId: number; isActive: boolean }) => {
      const response = await apiRequest('PUT', `/api/developer/api-keys/${keyId}`, { isActive });
      return response.json();
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'Success',
        description: `API key ${isActive ? 'activated' : 'deactivated'}`,
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update API key',
        variant: 'destructive',
      });
    },
  });

  const deleteApiKeyMutation = useMutation({
    mutationFn: async (keyId: number) => {
      const response = await apiRequest('DELETE', `/api/developer/api-keys/${keyId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/developer/api-keys'] });
      toast({
        title: 'Success',
        description: 'API key deleted successfully',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete API key',
        variant: 'destructive',
      });
    },
  });

  const createApiKey = () => {
    createApiKeyMutation.mutate({
      keyName: newKeyName,
      permissions: newKeyPermissions,
      expiresAt: newKeyExpiresAt || undefined,
    });
  };

  const toggleApiKeyStatus = (keyId: number, isActive: boolean) => {
    toggleApiKeyMutation.mutate({ keyId, isActive });
  };

  const deleteApiKey = (keyId: number) => {
    setKeyToDelete(keyId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteApiKey = () => {
    if (keyToDelete) {
      deleteApiKeyMutation.mutate(keyToDelete);
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'API key copied to clipboard',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'suspended':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <DeveloperNav />
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-gray-900">Developer Dashboard</CardTitle>
              <CardDescription>
                Please log in to access your developer dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full" data-testid="button-login">
                <Link href="/auth">Log In</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <DeveloperNav />
        <div className="flex items-center justify-center p-4 pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading developer dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!developerAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <DeveloperNav />
        <div className="flex items-center justify-center p-4 pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  if (developerAccount.status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <DeveloperNav />
        <div className="p-4 pt-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardHeader className="text-center pb-4">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-yellow-600" />
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  Application Under Review
                </CardTitle>
                <div className="flex justify-center mb-4">
                  {getStatusBadge('pending')}
                </div>
                <CardDescription className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Your developer account application is being reviewed by our team.
                  We typically process applications within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert className="bg-white border-yellow-200">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-gray-700">
                    You'll receive an email notification once your account is approved.
                    While you wait, explore our documentation to get familiar with the API.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Book className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Read Documentation</h3>
                          <p className="text-sm text-gray-600 mb-3">
                            Explore our comprehensive API documentation and get a head start on integration.
                          </p>
                          <Button asChild variant="outline" size="sm" data-testid="button-view-docs">
                            <Link href="/api-docs">
                              View Docs
                              <ExternalLink className="w-3 h-3 ml-2" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <HelpCircle className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">Need Help?</h3>
                          <p className="text-sm text-gray-600 mb-3">
                            Have questions about the review process? Our team is here to help.
                          </p>
                          <Button asChild variant="outline" size="sm" data-testid="button-contact-support">
                            <a href="mailto:developers@snugglecrafters.com">
                              Contact Support
                              <Mail className="w-3 h-3 ml-2" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Application Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {developerAccount.companyName && (
                      <div>
                        <span className="text-gray-500">Company:</span>
                        <span className="ml-2 text-gray-900">{developerAccount.companyName}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Applied:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(developerAccount.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {developerAccount.website && (
                      <div>
                        <span className="text-gray-500">Website:</span>
                        <a href={developerAccount.website} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                          {developerAccount.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (developerAccount.status === 'rejected' || developerAccount.status === 'suspended') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <DeveloperNav />
        <div className="p-4 pt-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
              <CardHeader className="text-center pb-4">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  {developerAccount.status === 'rejected' ? 'Application Not Approved' : 'Account Suspended'}
                </CardTitle>
                <div className="flex justify-center mb-4">
                  {getStatusBadge(developerAccount.status)}
                </div>
                <CardDescription className="text-lg text-gray-600 max-w-2xl mx-auto">
                  {developerAccount.status === 'rejected' 
                    ? 'Unfortunately, your developer account application was not approved at this time.'
                    : 'Your developer account has been suspended.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {developerAccount.rejectionReason && (
                  <Alert variant="destructive" className="bg-white border-red-200">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <span className="font-semibold">Reason:</span> {developerAccount.rejectionReason}
                    </AlertDescription>
                  </Alert>
                )}

                <Card className="bg-white">
                  <CardContent className="p-6">
                    <div className="text-center">
                      <h3 className="font-semibold text-gray-900 mb-2">Need Assistance?</h3>
                      <p className="text-gray-600 mb-4">
                        If you believe this was a mistake or would like to discuss your application,
                        please contact our support team.
                      </p>
                      <Button asChild data-testid="button-contact-support">
                        <a href="mailto:developers@snugglecrafters.com">
                          <Mail className="w-4 h-4 mr-2" />
                          Contact Support
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    You can also reapply with updated information by visiting our{' '}
                    <Link href="/developer-signup" className="text-blue-600 hover:underline">
                      developer signup page
                    </Link>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <DeveloperNav />
      <div className="p-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2" data-testid="text-dashboard-title">
              Developer Dashboard
            </h1>
            <p className="text-lg sm:text-xl text-gray-600">
              Manage your API access and monitor usage
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <Card data-testid="card-account-status">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Account Status</p>
                    <div className="mt-1">
                      {getStatusBadge(developerAccount.status)}
                    </div>
                  </div>
                  <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="card-api-keys-count">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">API Keys</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{apiKeys.length}</p>
                  </div>
                  <Key className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="card-monthly-calls">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Monthly Calls</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {usageStats?.totalCalls?.toLocaleString() || 0}
                    </p>
                  </div>
                  <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="card-plan-tier">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Plan</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">
                      {developerAccount.tier}
                    </p>
                  </div>
                  <Code className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="keys" className="space-y-4 sm:space-y-6">
            <div className="overflow-x-auto">
              <TabsList className="grid w-full grid-cols-3 min-w-[300px] h-12">
                <TabsTrigger value="keys" className="text-xs sm:text-sm h-10" data-testid="tab-api-keys">
                  API Keys
                </TabsTrigger>
                <TabsTrigger value="usage" className="text-xs sm:text-sm h-10" data-testid="tab-usage">
                  Usage
                </TabsTrigger>
                <TabsTrigger value="support" className="text-xs sm:text-sm h-10" data-testid="tab-support">
                  Support
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="keys" className="space-y-4 sm:space-y-6">
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg sm:text-xl">API Keys</CardTitle>
                      <CardDescription className="text-sm sm:text-base">
                        Manage your API keys for accessing our services
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setShowCreateKey(true)}
                      className="w-full sm:w-auto h-10 text-sm font-medium flex-shrink-0"
                      data-testid="button-create-key"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Key
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  {apiKeys.length === 0 ? (
                    <div className="text-center py-8">
                      <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 text-sm sm:text-base">No API keys created yet</p>
                      <Button 
                        className="mt-4 h-10 text-sm font-medium" 
                        onClick={() => setShowCreateKey(true)}
                        data-testid="button-create-first-key"
                      >
                        Create Your First API Key
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {apiKeys.map((key) => (
                        <div key={key.id} className="border rounded-lg p-4 bg-gray-50 sm:bg-white" data-testid={`api-key-${key.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                <h3 className="font-medium text-sm sm:text-base truncate">{key.keyName}</h3>
                                <Badge variant={key.isActive ? "default" : "secondary"} className="self-start">
                                  {key.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 font-mono break-all">
                                {key.keyPrefix}••••••••
                              </p>
                              <p className="text-xs text-gray-500 mt-2">
                                Created: {new Date(key.createdAt).toLocaleDateString()}
                                {key.lastUsedAt && ` • Last used: ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Switch
                                checked={key.isActive}
                                onCheckedChange={(checked) => toggleApiKeyStatus(key.id, checked)}
                                data-testid={`switch-key-${key.id}`}
                              />
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deleteApiKey(key.id)}
                                data-testid={`button-delete-key-${key.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="usage" className="space-y-4 sm:space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Usage Statistics</CardTitle>
                  <CardDescription>Monitor your API usage and performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600 font-medium">Successful Calls</p>
                      <p className="text-2xl font-bold text-green-700">
                        {usageStats?.successfulCalls?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600 font-medium">Error Calls</p>
                      <p className="text-2xl font-bold text-red-700">
                        {usageStats?.errorCalls?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 font-medium">Avg Response Time</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {usageStats?.averageResponseTime?.toFixed(0) || 0}ms
                      </p>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-4">Monthly Limit</h4>
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                      <div 
                        className="bg-blue-600 h-4 rounded-full transition-all" 
                        style={{ 
                          width: `${Math.min(((usageStats?.totalCalls || 0) / developerAccount.monthlyLimit) * 100, 100)}%` 
                        }}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      {usageStats?.totalCalls?.toLocaleString() || 0} / {developerAccount.monthlyLimit.toLocaleString()} calls used
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="support" className="space-y-4 sm:space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Developer Support</CardTitle>
                  <CardDescription>Get help with API integration and technical questions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Book className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">Documentation</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Comprehensive API documentation with examples and guides.
                            </p>
                            <Button asChild variant="outline" size="sm" data-testid="button-view-docs">
                              <Link href="/api-docs">
                                View Docs
                                <ExternalLink className="w-3 h-3 ml-2" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Mail className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">Create Ticket</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              Get personalized help from our developer support team.
                            </p>
                            <Button asChild variant="outline" size="sm" data-testid="button-create-ticket">
                              <a href="mailto:developers@snugglecrafters.com?subject=Developer Support Request">
                                Contact Support
                                <Mail className="w-3 h-3 ml-2" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={showCreateKey} onOpenChange={setShowCreateKey}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key to access our services.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="keyName">Key Name</Label>
              <Input
                id="keyName"
                placeholder="e.g., Production Key"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                data-testid="input-key-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiresAt">Expiration Date (Optional)</Label>
              <Input
                id="expiresAt"
                type="date"
                value={newKeyExpiresAt}
                onChange={(e) => setNewKeyExpiresAt(e.target.value)}
                data-testid="input-expires-at"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateKey(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createApiKey} 
              disabled={!newKeyName || createApiKeyMutation.isPending}
              data-testid="button-confirm-create-key"
            >
              {createApiKeyMutation.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showApiKey} onOpenChange={setShowApiKey}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Copy your API key now. For security reasons, it won't be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg font-mono text-sm break-all">
              <span className="flex-1">{createdApiKey}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => createdApiKey && copyToClipboard(createdApiKey)}
                data-testid="button-copy-key"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowApiKey(false)} data-testid="button-close-key-dialog">
              I've Saved My Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this API key? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteApiKey}
              disabled={deleteApiKeyMutation.isPending}
              data-testid="button-confirm-delete-key"
            >
              {deleteApiKeyMutation.isPending ? 'Deleting...' : 'Delete Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
